import type { BraceletPatternDto, TrackingStatusDto } from '@miyuki/types';

import { assertRowIndex } from './pattern.entity';

/**
 * El avance del tejido.
 *
 * Es **un entero**: cuántas filas están dadas por tejidas, contadas desde la
 * fila 1 y sin huecos (RF-12). Todo lo demás se deriva de ahí y no se almacena,
 * igual que la numeración de filas de la feature 001 y por el mismo motivo: un
 * contador aparte es un contador que puede desincronizarse.
 *
 * Ninguna de estas operaciones devuelve un `PatternEdit`. El avance no entra al
 * historial de edición —tiene el suyo, en `PatternSession`— porque deshacer una
 * fila tejida y deshacer una mostacilla pintada son cosas distintas y RF-47
 * exige no confundirlas.
 */

/** Acota a `[0, rows.length]`. Es la red que sostiene la invariante I8. */
export function clampProgress(pattern: BraceletPatternDto, value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(Math.trunc(value), 0), pattern.rows.length);
}

/**
 * Da por tejida la fila actual y pasa a la siguiente.
 *
 * Con el diseño terminado no hace nada: no existe una fila siguiente que
 * confirmar, y devolver el mismo patrón evita que una pulsación de más en el
 * botón grande empuje el avance fuera de rango (RF-18).
 */
export function completeRow(pattern: BraceletPatternDto): BraceletPatternDto {
  if (pattern.progress >= pattern.rows.length) return pattern;
  return { ...pattern, progress: pattern.progress + 1 };
}

/**
 * Sitúa el seguimiento en una fila: las anteriores quedan dadas por tejidas y
 * esa y las siguientes, pendientes (RF-51a). Va en los dos sentidos (RF-51b).
 *
 * `row` es el índice base 0. Una fila que no existe es un error de la usuaria,
 * no un valor a acotar en silencio: acotarlo la dejaría en una fila que no pidió
 * sin decírselo (RF-51e).
 */
export function goToRow(pattern: BraceletPatternDto, row: number): BraceletPatternDto {
  assertRowIndex(pattern, row);
  return { ...pattern, progress: row };
}

/** Devuelve el avance a cero para volver a tejer la misma pieza (RF-19). */
export function resetProgress(pattern: BraceletPatternDto): BraceletPatternDto {
  return { ...pattern, progress: 0 };
}

/** Cambia el sentido de lectura sin tocar el avance ni la cuadrícula (RF-28e). */
export function setReadingAlternates(
  pattern: BraceletPatternDto,
  alternates: boolean,
): BraceletPatternDto {
  if (alternates === pattern.readingAlternates) return pattern;
  return { ...pattern, readingAlternates: alternates };
}

/**
 * Todo lo que la pantalla informa, en una sola pasada.
 *
 * El porcentaje se mide sobre **filas** y no sobre mostacillas, que es la
 * unidad de avance (A-02): así no puede contradecir al contador de filas que
 * falta, y un diseño enteramente vacío no da cero sobre cero. Las mostacillas
 * restantes van aparte, rotuladas como otro dato, porque sirven para saber si
 * alcanzan las cuentas que hay (RF-24).
 */
export function trackingStatus(pattern: BraceletPatternDto): TrackingStatusDto {
  const totalRows = pattern.rows.length;
  const completedRows = clampProgress(pattern, pattern.progress);
  const finished = completedRows >= totalRows;

  let beadsRemaining = 0;
  for (let row = completedRows; row < totalRows; row += 1) {
    for (const cell of pattern.rows[row] ?? []) {
      if (cell !== null) beadsRemaining += 1;
    }
  }

  return {
    currentRow: finished ? null : completedRows,
    totalRows,
    completedRows,
    ratio: totalRows === 0 ? 0 : completedRows / totalRows,
    rowsRemaining: totalRows - completedRows,
    beadsRemaining,
    finished,
  };
}
